import React, { useRef, forwardRef, useImperativeHandle, useCallback } from 'react'
import { makeStyles } from '@mui/styles'
import UploadButton from '@rpldy/upload-button'
import UploadDropZone from '@rpldy/upload-drop-zone'

import { DMS_UPLOAD_ALLOWED_EXTENSIONS } from '@/redux/ducks/document/upload'
// FLAG:useDocuAi (uploadHardening) — gates the className passthrough below.
import { isUploadHardeningEnabled } from '@/redux/ducks/document/_helpers/uploadHardening'
import UploadyWrapper from '@/modules/base/components/uploady-wrapper/uploady-wrapper'

const useStyles = makeStyles(() => ({
  buttonWrapper: {
    visibility: 'hidden',
  },
}))

interface DocumentsUploadTriggerRef {
  triggerUpload: () => void
}

interface DocumentsUploadTriggerProps {
  multiple: boolean
  children: React.ReactNode
  className?
  allowedExtensions?: string[]
  onSubmit?
  disabled?: boolean
}

const UploadTrigger = forwardRef<HTMLButtonElement, { onUpload:() => void, classes }>(
  ({ onUpload, classes }, ref) => {
    return (
      <div className={classes.buttonWrapper}>
        <UploadButton
          onClick={onUpload}
          ref={ref}
        />
      </div>
    )
  },
)

const DocumentsUploadTrigger = forwardRef<DocumentsUploadTriggerRef, DocumentsUploadTriggerProps>(
  ({ multiple, children, className, allowedExtensions = DMS_UPLOAD_ALLOWED_EXTENSIONS, onSubmit, disabled }, ref) => {
    const classes = useStyles()
    const uploadTriggerRef = useRef<HTMLButtonElement>(null)

    const onUpload = useCallback((e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      if (!disabled) {
        uploadTriggerRef.current?.click()
      }
    }, [disabled])

    useImperativeHandle(ref, () => ({
      triggerUpload: onUpload,
    }), [onUpload])

    return (
      <UploadyWrapper
        ref={ref}
        multiple={multiple}
        allowedExtensions={allowedExtensions}
        onSubmit={onSubmit}
        disabled={disabled}
        uploadTriggerRef={uploadTriggerRef}
      >
        <UploadTrigger
          ref={uploadTriggerRef}
          onUpload={onUpload}
          classes={classes}
        />
        <UploadDropZone
          // `className` was declared but never passed through: every drop zone that set its size through a
          // class silently lost it. In the receipt form (receipt-file-uploader), this meant `flex: 1` never
          // arrived — the area shrank to its content instead of filling the column, while without the flag
          // it occupies the entire area.
          // Gated (F15.5): with the flag OFF, the class continues to be discarded — the flyout retry trigger
          // (pending-upload) has always supplied one too, and applying it for the first time would be an
          // ungated visual deviation from the baseline.
          className={isUploadHardeningEnabled() ? className : undefined}
          onDragOverClassName="dropzoneActive"
          extraProps={{
            onClick: (e: React.MouseEvent) => {
              if (!uploadTriggerRef.current?.contains(e.target as Node)) {
                onUpload(e)
              }
            },
          }}
        >
          {children}
        </UploadDropZone>
      </UploadyWrapper>
    )
  },
)

export default DocumentsUploadTrigger
